/**
 * Reglas del dashboard de la Pasarela de Pagos: todo lo que DECIDE un número vive
 * acá, sin Angular, para probarlo sin TestBed. TypeScript puro; los tipos de
 * entrada son estructurales (no se importa `shared/models`), igual que
 * `payments-summary/payments-breakdown` y `payments-links/links-kpi`.
 *
 * Tres reglas de dinero atraviesan el archivo:
 *  1. Un monto "sin dato" (`null`) nunca se vuelve 0 — se pinta "—".
 *  2. Una moneda nunca se suma con otra: los recuadros leen SOLO MXN.
 *  3. Los porcentajes se calculan sobre las MISMAS cuentas que se pintan, así
 *     que la dona y su leyenda siempre suman lo que se ve.
 */
import type { SummaryBucketLike } from '../payments-summary/payments-breakdown';
import type {
  PaymentsAmountCount,
  PaymentsChartSeries,
  PaymentsRecentOperation,
  PaymentsSeriePoint,
  PaymentsStatusKey,
  PaymentsStatusRow,
  PaymentsStatusSection,
  PaymentsType,
  PaymentsTypeRow,
  PaymentsTypeTotal,
  PaymentsTypeTotals,
} from './payments-dashboard.model';

/** Hoy todo es MXN; mismo criterio que `LINKS_KPI_CURRENCY`. */
export const PAYMENTS_DASHBOARD_CURRENCY = 'MXN';

/** Cuántas operaciones recientes se piden y se pintan por vista. */
export const PAYMENTS_RECENT_LIMIT = 6;

/** Sección `links` de `/summary/`: moneda → estado → bucket (montos string). */
export type PaymentsLinksSectionLike =
  | Readonly<Record<string, Readonly<Record<string, SummaryBucketLike | undefined>> | undefined>>
  | null
  | undefined;

/** Lo que se lee de un renglón de `GET /links/` (estructural: `PaymentLinkResponse` encaja). */
export interface PaymentsLinkRowLike {
  readonly id_link: number;
  readonly description?: string;
  readonly amount: string | number | null;
  readonly currency?: string;
  readonly status: string;
  readonly created_at?: string;
}

/** Lo que se lee de un cargo del listado (estructural: `PaymentChargeListRow` encaja). */
export interface PaymentsChargeRowLike {
  readonly id: string;
  readonly idTransaction: string;
  readonly description: string;
  readonly amount: number;
  readonly currency: string;
  /** Estado ya normalizado por el mapper (`''` si no se reconoció). */
  readonly status: string;
  readonly createdAt: string;
}

/** Lo que se lee de un QR del listado (estructural: `PaymentQr` encaja). */
export interface PaymentsQrRowLike {
  readonly idQr: number;
  readonly reference: string;
  readonly description: string;
  readonly status: string;
  readonly amount: number;
  readonly currency: string;
  readonly createdAt: string | null;
}

const CERO: PaymentsAmountCount = { count: 0, amount: 0 };

function finitoONaN(raw: unknown): number {
  const n = typeof raw === 'string' ? (raw.trim() === '' ? NaN : Number(raw)) : raw;
  return typeof n === 'number' && Number.isFinite(n) ? n : NaN;
}

function aCentavos(monto: number): number {
  return Math.round(monto * 100);
}

/** Porcentaje a un decimal; mismo redondeo que la dona de "Todo". Total 0 → 0. */
export function porcentaje(count: number, total: number): number {
  return total > 0 ? Math.round((count * 1000) / total) / 10 : 0;
}

/**
 * Suma de los montos CON dato. Si todas las partes son "sin dato" (o no hay
 * partes) el resultado también es "sin dato". Se suma en centavos para no
 * arrastrar errores de coma flotante (0.1 + 0.2 da 0.3, no 0.30000000000000004).
 */
export function sumarMontos(partes: readonly (number | null)[]): number | null {
  let centavos = 0;
  let algunoConDato = false;
  for (const parte of partes) {
    if (parte === null) continue;
    algunoConDato = true;
    centavos += aCentavos(parte);
  }
  return algunoConDato ? centavos / 100 : null;
}

function conPorcentajes(
  filas: readonly { key: PaymentsStatusKey; count: number; amount: number | null }[],
): PaymentsStatusRow[] {
  const total = filas.reduce((suma, f) => suma + f.count, 0);
  return filas.map((f) => ({ ...f, percentage: porcentaje(f.count, total) }));
}

/** Agrupa estados en un renglón: conteo sumado y monto con `sumarMontos` sobre los PRESENTES. */
function agrupar(
  estados: Readonly<Record<string, PaymentsAmountCount>> | undefined,
  nombres: readonly string[],
): { count: number; amount: number | null } {
  const presentes = nombres
    .map((n) => estados?.[n])
    .filter((b): b is PaymentsAmountCount => b !== undefined);
  // En una respuesta exitosa la ausencia es un cero verdadero (R16).
  if (presentes.length === 0) return { ...CERO };
  return {
    count: presentes.reduce((suma, b) => suma + b.count, 0),
    amount: sumarMontos(presentes.map((b) => b.amount)),
  };
}

const ESTADOS_DE_LINK: readonly { key: PaymentsStatusKey; status: string }[] = [
  { key: 'paid', status: 'Completed' },
  { key: 'pending', status: 'Pending' },
  { key: 'expired', status: 'Expired' },
  { key: 'cancelled', status: 'Cancelled' },
];

/**
 * Links por estado (sección `links` del total de `/summary/`). Cuatro renglones
 * en orden fijo; el denominador del porcentaje es la suma de ESOS cuatro (un
 * estado extra que mande el back no infla el total de la dona).
 */
export function linksPorEstado(
  links: PaymentsLinksSectionLike,
  moneda: string = PAYMENTS_DASHBOARD_CURRENCY,
): PaymentsStatusRow[] {
  const buckets = links?.[moneda] ?? undefined;
  return conPorcentajes(
    ESTADOS_DE_LINK.map(({ key, status }) => {
      const bucket = buckets?.[status];
      if (!bucket) return { key, ...CERO };
      const conteo = finitoONaN(bucket.count);
      const monto = finitoONaN(bucket.amount);
      return {
        key,
        count: Number.isNaN(conteo) ? 0 : conteo,
        amount: Number.isNaN(monto) ? null : monto,
      };
    }),
  );
}

/**
 * QR emitidos por estado. `Reserved` es un `Pending` con el cobro en curso, así
 * que se agrupan en "Pendiente". Un estado fuera de los cinco del contrato no
 * aparece ni cuenta en el denominador.
 */
export function qrPorEstado(
  qr: PaymentsStatusSection,
  moneda: string = PAYMENTS_DASHBOARD_CURRENCY,
): PaymentsStatusRow[] {
  const estados = qr?.[moneda];
  return conPorcentajes([
    { key: 'paid', ...agrupar(estados, ['Paid']) },
    { key: 'pending', ...agrupar(estados, ['Pending', 'Reserved']) },
    { key: 'cancelled', ...agrupar(estados, ['Cancelled']) },
    { key: 'overdue', ...agrupar(estados, ['Expired']) },
  ]);
}

/**
 * Dona de resultado del QR: exitosas contra canceladas. Los `Pending`/`Reserved`
 * todavía no tienen resultado, así que no entran (ni al denominador).
 */
export function qrResultado(
  qr: PaymentsStatusSection,
  moneda: string = PAYMENTS_DASHBOARD_CURRENCY,
): PaymentsStatusRow[] {
  const estados = qr?.[moneda];
  return conPorcentajes([
    { key: 'successful', ...agrupar(estados, ['Paid']) },
    { key: 'cancelledOps', ...agrupar(estados, ['Cancelled', 'Expired']) },
  ]);
}

/** Orden fijo de los tipos en TODA la vista (KPIs, líneas, dona, leyendas). */
export const PAYMENTS_TYPES: readonly PaymentsType[] = ['links', 'charges', 'qr'];

/** Campo de `serie` que corresponde a cada tipo. */
const CAMPO_DE_TIPO: Readonly<Record<PaymentsType, 'link' | 'charge' | 'qr'>> = {
  links: 'link',
  charges: 'charge',
  qr: 'qr',
};

const PUNTO_EN_CERO = {
  link: { count: 0, amount: 0 },
  charge: { count: 0, amount: 0 },
  qr: { count: 0, amount: 0 },
} as const;

/**
 * Completa el eje de `año` en el FRONT: el back solo manda los meses CON cobros
 * (`movements/series.normalizar` deja `año` y `custom` intactos), así que un año con
 * cobros solo en octubre llegaba como UN bucket y la gráfica se veía rota. Se arma
 * enero → mes de `hasta` (el `period.to` que resolvió el back, `YYYY-MM-DD`) del año de
 * `hasta`, con el formato de bucket del back para `año` (`YYYY-MM`), y cada mes sin
 * cobros va en cero — un cero VERDADERO: el back no lo mandó porque no hubo nada.
 *
 * Un bucket que no encaja en ese rango (o un `hasta` ilegible) no se descarta: se
 * conserva y el eje queda ordenado. Solo aplica a `año`; `custom` se deja como llega.
 */
export function completarEjeAnual(
  serie: readonly PaymentsSeriePoint[],
  hasta: string,
): PaymentsSeriePoint[] {
  const m = /^(\d{4})-(\d{2})/.exec(hasta ?? '');
  const anio = m ? Number(m[1]) : NaN;
  const mesFinal = m ? Number(m[2]) : NaN;
  if (!Number.isInteger(anio) || mesFinal < 1 || mesFinal > 12) return [...serie];

  const porBucket = new Map(serie.map((p) => [p.bucket, p]));
  const eje = Array.from({ length: mesFinal }, (_, i) => `${anio}-${String(i + 1).padStart(2, '0')}`);
  const enRango = new Set(eje);
  const completos = eje.map((bucket) => porBucket.get(bucket) ?? { bucket, ...PUNTO_EN_CERO });
  const fuera = serie.filter((p) => !enRango.has(p.bucket));
  return fuera.length === 0
    ? completos
    : [...completos, ...fuera].sort((a, b) => a.bucket.localeCompare(b.bucket));
}

/** Las tres series de lo cobrado (R19), en el orden de buckets recibido. */
export function seriesCobradas(serie: readonly PaymentsSeriePoint[]): PaymentsChartSeries[] {
  return PAYMENTS_TYPES.map((key) => ({
    key,
    points: serie.map((p) => {
      const valor = p[CAMPO_DE_TIPO[key]];
      return { bucket: p.bucket, amount: valor.amount, count: valor.count };
    }),
  }));
}

/**
 * Suma de montos de un indicador. Sin buckets no hay nada que sumar: es un cero
 * verdadero (el periodo no tuvo cobros). Con buckets, solo es "sin dato" si TODOS
 * los montos lo son (R43).
 */
function montoDelPeriodo(montos: readonly (number | null)[]): number | null {
  return montos.length === 0 ? 0 : sumarMontos(montos);
}

function totalDeTipo(serie: readonly PaymentsSeriePoint[], tipo: PaymentsType): PaymentsTypeTotal {
  const campo = CAMPO_DE_TIPO[tipo];
  return {
    count: serie.reduce((suma, p) => suma + p[campo].count, 0),
    amount: montoDelPeriodo(serie.map((p) => p[campo].amount)),
  };
}

/** Indicadores de la fila de KPIs: total cobrado y uno por tipo (R43). */
export function totalesPorTipo(serie: readonly PaymentsSeriePoint[]): PaymentsTypeTotals {
  const links = totalDeTipo(serie, 'links');
  const charges = totalDeTipo(serie, 'charges');
  const qr = totalDeTipo(serie, 'qr');
  return {
    total: {
      count: links.count + charges.count + qr.count,
      amount: montoDelPeriodo(serie.flatMap((p) => [p.link.amount, p.charge.amount, p.qr.amount])),
    },
    links,
    charges,
    qr,
  };
}

/** Dona "Tipo de pago más utilizado" (R44): por conteo, % sobre la suma de los tres. */
export function porcionesPorTipo(serie: readonly PaymentsSeriePoint[]): PaymentsTypeRow[] {
  const filas = PAYMENTS_TYPES.map((key) => ({ key, ...totalDeTipo(serie, key) }));
  const total = filas.reduce((suma, f) => suma + f.count, 0);
  return filas.map((f) => ({ ...f, percentage: porcentaje(f.count, total) }));
}

const ESTADO_DE_LINK = new Map<string, PaymentsStatusKey>([
  ['Completed', 'paid'],
  ['Pending', 'pending'],
  ['Expired', 'expired'],
  ['Cancelled', 'cancelled'],
]);

const ESTADO_DE_QR = new Map<string, PaymentsStatusKey>([
  ['Paid', 'paid'],
  ['Pending', 'pending'],
  ['Reserved', 'pending'],
  ['Cancelled', 'cancelled'],
  ['Expired', 'overdue'],
]);

function marcaDeTiempo(iso: string | null): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? -Infinity : t;
}

/**
 * Del más reciente al más antiguo (sin fecha al final) y a lo más 6. Un empate (o dos
 * renglones sin fecha) conserva el orden de entrada: restar dos `-Infinity` da `NaN` y
 * dejaría el orden a merced del motor.
 */
function ordenarYCortar(filas: readonly PaymentsRecentOperation[]): PaymentsRecentOperation[] {
  return filas
    .map((fila, i) => ({ fila, i, t: marcaDeTiempo(fila.occurredAt) }))
    .sort((a, b) => (a.t === b.t ? a.i - b.i : b.t - a.t))
    .slice(0, PAYMENTS_RECENT_LIMIT)
    .map(({ fila }) => fila);
}

/**
 * Tabla unificada (R26): une las fuentes, ordena por fecha y corta en 6. El empate se
 * resuelve por el orden de las fuentes (links, tarjeta, QR) y luego por su posición.
 */
export function fusionarRecientes(
  fuentes: readonly (readonly PaymentsRecentOperation[])[],
): PaymentsRecentOperation[] {
  return ordenarYCortar(fuentes.flat());
}

/** Links recientes. Un estado desconocido sale como `unknown` ("Sin estado"), nunca crudo. */
export function recientesDeLinks(
  filas: readonly PaymentsLinkRowLike[],
): PaymentsRecentOperation[] {
  return ordenarYCortar(
    filas.map((f) => {
      const monto = finitoONaN(f.amount);
      return {
        id: `link-${f.id_link}`,
        type: 'links' as const,
        reference: (f.description ?? '').trim() || String(f.id_link),
        statusKey: ESTADO_DE_LINK.get(f.status) ?? 'unknown',
        amount: Number.isNaN(monto) ? null : monto,
        currency: f.currency || PAYMENTS_DASHBOARD_CURRENCY,
        occurredAt: f.created_at ?? null,
      };
    }),
  );
}

/** QR recientes. `Expired` de un QR se lee "Vencido" (overdue), no "Expirado". */
export function recientesDeQr(filas: readonly PaymentsQrRowLike[]): PaymentsRecentOperation[] {
  return ordenarYCortar(
    filas.map((f) => {
      const monto = finitoONaN(f.amount);
      return {
        id: `qr-${f.idQr}`,
        type: 'qr' as const,
        reference: (f.reference ?? '').trim() || (f.description ?? '').trim() || String(f.idQr),
        statusKey: ESTADO_DE_QR.get(f.status) ?? 'unknown',
        amount: Number.isNaN(monto) ? null : monto,
        currency: f.currency || PAYMENTS_DASHBOARD_CURRENCY,
        occurredAt: f.createdAt ?? null,
      };
    }),
  );
}

/**
 * Pagos con tarjeta recientes. El estado viaja tal cual lo normalizó el mapper: la vista
 * lo etiqueta con el catálogo de la pantalla de Cargos (nunca el string crudo).
 */
export function recientesDeCargos(filas: readonly PaymentsChargeRowLike[]): PaymentsRecentOperation[] {
  return ordenarYCortar(
    filas.map((f) => {
      const monto = finitoONaN(f.amount);
      const id = f.idTransaction || f.id;
      return {
        id: `charge-${id}`,
        type: 'charges' as const,
        reference: (f.description ?? '').trim() || id,
        chargeStatus: f.status ?? '',
        amount: Number.isNaN(monto) ? null : monto,
        currency: f.currency || PAYMENTS_DASHBOARD_CURRENCY,
        occurredAt: f.createdAt || null,
      };
    }),
  );
}

/**
 * `YYYY-MM-DD` según la fecha LOCAL. `toISOString()` pasa a UTC: de noche en
 * CDMX corre el día y el back filtraría un periodo que el usuario no eligió.
 */
export function aFechaLocal(d: Date): string {
  const dd = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}`;
}

/** Rango a medida ordenado (el back rechaza `from > to` con 400). */
export function rangoOrdenado(a: Date, b: Date): { dateFrom: string; dateTo: string } {
  const [desde, hasta] = a.getTime() <= b.getTime() ? [a, b] : [b, a];
  return { dateFrom: aFechaLocal(desde), dateTo: aFechaLocal(hasta) };
}
